import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { courseAPI, progressAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { FiBook, FiUsers, FiClock, FiPlay, FiCheck, FiArrowLeft, FiAward } from 'react-icons/fi';

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    fetchCourse();
  }, [id]);

  const fetchCourse = async () => {
    setLoading(true);
    try {
      const res = await courseAPI.getCourse(id);
      setCourse(res.data.course);
      
      if (user) {
        try {
          const progressRes = await progressAPI.getCourseProgress(id);
          setProgress(progressRes.data.progress);
        } catch (err) {
          console.log('Not enrolled yet');
        }
      }
    } catch (error) {
      console.error('Failed to fetch course', error);
      // 不跳转到courses页面，使用模拟数据
      const mockCourse = {
        _id: id,
        title: '课程详情',
        lessons: [
          {
            _id: '1',
            title: '课程内容',
            type: '视频',
            duration: 5,
            content: {
              videoUrl: '' // 已删除视频URL
            }
          }
        ]
      };
      setCourse(mockCourse);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!user) {
      // 不强制登录，使用模拟数据
      console.log('User not logged in, using mock data');
      // 直接跳转到第一课
      if (course && course.lessons && course.lessons.length > 0) {
        navigate(`/lesson/${course._id}/${course.lessons[0]._id}`);
      }
      return;
    }

    setEnrolling(true);
    try {
      await courseAPI.enrollCourse(id);
      await fetchCourse();
    } catch (error) {
      console.error('Failed to enroll', error);
    } finally {
      setEnrolling(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      daily: '🗣️',
      business: '💼',
      travel: '✈️',
      grammar: '📝',
      pronunciation: '🔊',
      ielts: '📚',
      toefl: '🎓'
    };
    return icons[category] || '📖';
  };

  if (loading) {
    return (
      <div className="loading-container" style={{ minHeight: '60vh' }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!course) {
    return null;
  }

  const isEnrolled = !!progress;
  const completedLessons = progress?.lessons?.filter(l => l.completed).length || 0;
  const totalLessons = course.lessons?.length || 0;
  const progressPercent = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  return (
    <div style={{ height: '100vh', backgroundColor: '#ffffff', color: '#1a2b4c', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* 导航栏 */}
      <nav style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0,
        right: 0,
        zIndex: 1000, 
        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
        backdropFilter: 'blur(10px)',
        height: '64px',
        minHeight: '64px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center'
      }}>
        <style>{`
          @media (max-width: 1024px) {
            .nav-left-area {
              gap: 0.35rem !important;
            }
            .nav-logo-123, .nav-logo-wonder {
              font-size: 1.6rem !important;
            }
            .nav-category-label {
              font-size: clamp(0.8rem, 2.2vw, 1.1rem) !important;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              max-width: 40vw;
            }
          }
          @media (max-width: 768px) {
            .nav-left-area {
              gap: 0.25rem !important;
            }
            .nav-logo-123, .nav-logo-wonder {
              font-size: 1.4rem !important;
            }
            .nav-category-label {
              font-size: clamp(0.75rem, 2vw, 1rem) !important;
              max-width: 35vw;
            }
          }
          @media (max-width: 480px) {
            .nav-logo-123, .nav-logo-wonder {
              font-size: 1.2rem !important;
            }
            .nav-category-label {
              font-size: clamp(0.7rem, 1.8vw, 0.9rem) !important;
              max-width: 32vw;
            }
          }
        `}</style>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          width: '100%',
          maxWidth: '100%',
          margin: '0 auto',
          padding: '0 1rem',
          boxSizing: 'border-box'
        }}>
          {/* 左侧区域 - Logo */}
          <div className="nav-left-area" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div 
              style={{ 
                display: 'flex', 
                alignItems: 'center',
                textDecoration: 'none',
                color: '#333333',
                cursor: 'pointer',
                minWidth: 0,
                flexShrink: 1
              }}
              onClick={() => navigate('/')}
            >
              <img 
                src="/logo.png" 
                alt="123wonder英文科普" 
                style={{ 
                  height: 'clamp(40px, 6vw, 50px)', 
                  width: 'auto',
                  maxWidth: '100%'
                }} 
              />
            </div>
          </div>
        </div>
      </nav>

      <div style={{ flex: 1, overflowY: 'auto', paddingTop: '64px' }}>
        <div className="page-header" style={{ padding: '30px 0', backgroundColor: '#ffffff' }}>
        <div className="container">
          <button 
            onClick={() => navigate('/courses')} 
            className="btn btn-ghost"
            style={{ 
              marginBottom: '20px', 
              padding: '8px 16px',
              backgroundColor: '#f8f9fa',
              color: '#1a2b4c',
              border: '1px solid #e9ecef',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#e9ecef'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#f8f9fa'}
          >
            <FiArrowLeft /> 返回课程列表
          </button>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '30px' }}>
            <div style={{ 
              width: '120px', 
              height: '120px', 
              background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '60px',
              flexShrink: 0,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
            }}>
              {getCategoryIcon(course.category)}
            </div>
            <div style={{ flex: 1 }}>
              <h1 className="page-header-title" style={{ marginBottom: '10px', color: '#1a2b4c' }}>{course.title}</h1>
              <p className="page-header-text" style={{ marginBottom: '20px', color: '#495057' }}>{course.description}</p>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <span style={{ 
                  fontSize: '14px', 
                  padding: '6px 16px',
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                  borderRadius: '16px',
                  fontWeight: '500'
                }}>
                  {course.level}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#495057' }}>
                  <FiBook /> {totalLessons} 课时
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#495057' }}>
                  <FiUsers /> {course.enrolledCount} 人已学习
                </span>
                {course.instructor && (
                  <span style={{ color: '#495057' }}>
                    讲师: {course.instructor.fullName || course.instructor.username}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="section" style={{ backgroundColor: '#f8f9fa', padding: '40px 0' }}>
        <div className="container">
          {isEnrolled && (
            <div style={{ 
              padding: '24px', 
              marginBottom: '30px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1a2b4c' }}>学习进度</h3>
                <span style={{ fontWeight: '600', color: '#1976d2' }}>{progressPercent}% 完成</span>
              </div>
              <div style={{ 
                height: '10px', 
                backgroundColor: '#e9ecef',
                borderRadius: '5px',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  width: `${progressPercent}%`,
                  backgroundColor: '#1976d2',
                  height: '100%',
                  transition: 'width 0.5s ease'
                }}></div>
              </div>
              <p style={{ marginTop: '8px', color: '#6c757d', fontSize: '14px' }}>
                已完成 {completedLessons} / {totalLessons} 课
              </p>
            </div>
          )}

          <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1', minWidth: '300px' }}>
              <div style={{ 
                padding: '30px', 
                marginBottom: '24px',
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
              }}>
                <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#1a2b4c' }}>课程内容</h3>
                {course.lessons && course.lessons.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {course.lessons.map((lesson, index) => {
                      const lessonProgress = progress?.lessons?.find(l => l.lesson?._id === lesson._id);
                      const isCompleted = lessonProgress?.completed;
                      
                      return (
                        <div 
                          key={lesson._id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            padding: '16px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                          onClick={() => {
                            // 直接跳转到课程，不需要登录
                            navigate(`/lesson/${course._id}/${lesson._id}`);
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#e9ecef'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                        >
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: isCompleted ? '#4caf50' : '#1976d2',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: '600',
                            flexShrink: 0
                          }}>
                            {isCompleted ? <FiCheck /> : index + 1}
                          </div>
                          <div style={{ flex: 1 }}>
                            <h4 style={{ fontWeight: '600', marginBottom: '4px', color: '#1a2b4c' }}>{lesson.title}</h4>
                            <div style={{ display: 'flex', gap: '12px', fontSize: '13px', color: '#6c757d' }}>
                              <span>{lesson.type}</span>
                              <span>{lesson.duration} 分钟</span>
                            </div>
                          </div>
                          <FiPlay style={{ color: '#1976d2' }} />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ color: '#6c757d', textAlign: 'center', padding: '20px' }}>
                    暂无课程内容
                  </p>
                )}
              </div>
            </div>

            <div style={{ width: '300px', flexShrink: 0 }}>
              <div style={{ 
                padding: '30px', 
                position: 'sticky', 
                top: '90px',
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
              }}>
                <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px', color: '#1a2b4c' }}>
                  开始学习
                </h3>
                <p style={{ color: '#6c757d', marginBottom: '20px' }}>
                  访问全部 {totalLessons} 课
                </p>
                {course.lessons && course.lessons.length > 0 && (
                  <Link 
                    to={`/lesson/${course._id}/${course.lessons[0]._id}`}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '12px 24px',
                      backgroundColor: '#1976d2',
                      color: 'white',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      textAlign: 'center',
                      fontWeight: '600',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#1565c0'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#1976d2'}
                  >
                    <FiPlay /> 开始学习
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      </div>
    </div>
  );
};

export default CourseDetail;
